// Full-screen triangle; the fragment shader ray-casts the sphere per pixel.
export const GLOBE_VS = `#version 300 es
in vec2 p;
void main() { gl_Position = vec4(p, 0.0, 1.0); }`;

// The land texture (/elite/globe-land.png) holds the blurred land mask in R and the
// country id in G. Land = big dots, sea = tiny dots, a faint 15° graticule on top.
export const GLOBE_FS = `#version 300 es
precision highp float;
uniform vec2 uRes;      // canvas size, CSS px
uniform vec2 uC;        // sphere centre, CSS px
uniform float uR, uRot, uTilt, uDpr;
uniform sampler2D uTerra;
uniform vec2 uMouse;
uniform float uHover, uSpot;
uniform float uPais, uSel;
uniform vec3 uCor;
uniform vec3 uAuto, uAutoS;
uniform vec2 uAutoC[3];
out vec4 o;

vec3 dir(float lat, float lon) {
  return vec3(cos(radians(lat)) * sin(radians(lon)), sin(radians(lat)), cos(radians(lat)) * cos(radians(lon)));
}

void main() {
  vec2 px = gl_FragCoord.xy / uDpr; px.y = uRes.y - px.y;
  vec2 q = (px - uC) / uR; q.y = -q.y;
  float r2 = dot(q, q);
  if (r2 > 1.0) { o = vec4(0.0); return; }

  float z = sqrt(1.0 - r2), ct = cos(uTilt), st = sin(uTilt);
  float y0 = q.y * ct + z * st, z0 = -q.y * st + z * ct;
  float lat = degrees(asin(clamp(y0, -1.0, 1.0)));
  float lon = degrees(atan(q.x, z0)) - uRot;

  vec2 uv = vec2(fract((lon + 180.0) / 360.0), (90.0 - lat) / 180.0);
  float terra = texture(uTerra, uv).r;
  ivec2 ts = textureSize(uTerra, 0);
  ivec2 tc = ivec2(min(uv * vec2(ts), vec2(ts) - 1.0));
  float id = floor(texelFetch(uTerra, tc, 0).g * 255.0 + 0.5);

  // hovered country, plus the countries of the pinned cities (only within ~30° of the city,
  // so Alaska/Hawaii do not light up with LA/NY)
  float sel = (1.0 - step(0.5, abs(id - uPais))) * uSel * terra;
  vec3 aqui = dir(lat, lon);
  for (int k = 0; k < 3; k++) {
    float perto = smoothstep(0.82, 0.9, dot(aqui, dir(uAutoC[k].x, uAutoC[k].y)));
    sel = max(sel, (1.0 - step(0.5, abs(id - uAuto[k]))) * uAutoS[k] * terra * perto);
  }

  // dots pinned to the surface: latitude rings, longitude step widens with cos(lat)
  const float PASSO = 0.82;
  float latc = floor(lat / PASSO + 0.5) * PASSO;
  float nAnel = max(1.0, floor(360.0 * cos(radians(latc)) / PASSO));
  float passoLon = 360.0 / nAnel, lonW = lon + 720.0;
  float lonc = floor(lonW / passoLon + 0.5) * passoLon;
  vec2 dd = vec2((lonW - lonc) * cos(radians(lat)), lat - latc);
  float dist = length(dd), aa = fwidth(dist) * 0.9;

  float spot = (1.0 - smoothstep(0.0, uSpot, distance(px, uMouse))) * uHover;
  float raio = mix(0.065, 0.21, smoothstep(0.35, 0.65, terra)) * (1.0 + spot * 0.45);
  float fio = 1.0 - smoothstep(raio - aa, raio + aa, dist);
  fio *= 1.0 - smoothstep(0.35, 0.9, aa / PASSO);          // dots that smear into lines at the rim fade out
  float forca = mix(0.16, 0.9, smoothstep(0.35, 0.65, terra));

  float base = fio * forca * (0.55 + 0.45 * pow(z, 0.5)) * smoothstep(0.0, 0.1, z);
  float a = clamp(base * (1.0 + spot * 0.9 + sel * 0.9), 0.0, 1.0);
  float verde = clamp(spot * (0.25 + terra * 0.35) + sel * 0.95, 0.0, 1.0);
  vec3 tinta = vec3(0.078, 0.094, 0.086);
  vec3 cor = mix(tinta, uCor, verde);

  // solid disc in the page tone, soft edge
  float disco = 1.0 - smoothstep(0.988, 1.0, r2);
  vec3 col = mix(vec3(0.957, 0.965, 0.957), cor, a);

  // graticule every 15°, faded where meridians bunch up
  vec2 gg = vec2(lon + 720.0, lat) / 15.0, gd = abs(fract(gg + 0.5) - 0.5), gw = fwidth(gg);
  float gx = (1.0 - smoothstep(0.0, gw.x * 1.1, gd.x)) * (1.0 - smoothstep(0.08, 0.3, gw.x));
  float gy = 1.0 - smoothstep(0.0, gw.y * 1.1, gd.y);
  float grade = max(gx, gy) * smoothstep(0.0, 0.22, z);
  col = mix(col, mix(tinta, uCor, clamp(spot * 1.3, 0.0, 1.0)), grade * (0.07 + spot * 0.22));

  col = mix(col, vec3(1.0), pow(1.0 - z, 4.0) * 0.35);     // white sheen on the rim
  float borda = 1.0 - smoothstep(0.0, 1.4 / uR, 1.0 - sqrt(r2));
  col = mix(col, tinta, borda * 0.5);                         // thin outline
  o = vec4(col * disco, disco);
}`;
